import { baseURL } from "@/services/api";
import { GetServerSideProps, GetStaticProps } from "next";
import Head from "next/head";
import Link from "next/link";
import styled from "styled-components";
import { ChangeEvent, FormEvent, useState } from "react";
import useUserRegister from "@/hooks/user/userRegister";
import ReactCodeInput from "react-code-input";
import EyeClosed from "@/assets/img/eyeClosed.svg";
import nookies, { parseCookies } from "nookies";
import useSettingsAll from "@/hooks/common/useSettingsAll";
import { SITE_NAME } from "@/helper/constant";
import { t, localePrefix } from "@/helper/helper";

interface Props {
  locale: string;
}

export default function Register({ locale }: Props) {
  const [params, setParams] = useState<{
    phone: string;
  }>({
    phone: "",
  });

  const [isPassword, setIsPassword] = useState<boolean>(false);

  const {
    postRegister,
    isLoading,
    error,
    isSuccess,
    postVerifyPhone,
    errorVerify,
    isLoadingPassword,
    errorPassword,
    postSetPassword,
  } = useUserRegister({
    setIsPassword,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!isLoading) postRegister(params);
  };

  const [code, setCode] = useState<string>("");

  const handleVerify = () => {
    postVerifyPhone({
      phone: "994" + params.phone,
      code,
    });
  };

  const [type, setType] = useState<string>("password");

  const handleType = () => {
    type === "password" ? setType("text") : setType("password");
  };

  const [password, setPassword] = useState<string>("");

  const handleSetPassword = () => {
    const { uuid } = parseCookies();

    if (!isLoadingPassword) {
      postSetPassword({
        phone: "994" + params.phone,
        code,
        password,
        uuid,
      });
    }
  };

  const { settings, isLoadingSettings } = useSettingsAll();

  return (
    <>
      <Head>
        <title>{SITE_NAME} Register</title>
      </Head>
      <Wrapper>
        <div className="login register">
          <div className="login-img-container">
            {!isLoadingSettings && (
              <img src={settings?.data.register_img} alt="Login" />
            )}
          </div>
          {!isPassword ? (
            !isSuccess ? (
              <form className="login-form" onSubmit={handleSubmit}>
                <div className="header">
                  <h1 className="title">{t("createNewAccount", locale)}</h1>
                  <h3 className="subtitle">
                    {t("confirmMobileNumber", locale)}
                  </h3>
                  <div className="google-facebook">
                    <a href="https://api.promusic.az/auth/google">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M23.7666 9.6498H22.8V9.6H12V14.4H18.7818C17.7924 17.1942 15.1338 19.2 12 19.2C8.0238 19.2 4.8 15.9762 4.8 12C4.8 8.0238 8.0238 4.8 12 4.8C13.8354 4.8 15.5052 5.4924 16.7766 6.6234L20.1708 3.2292C18.0276 1.2318 15.1608 0 12 0C5.373 0 0 5.373 0 12C0 18.627 5.373 24 12 24C18.627 24 24 18.627 24 12C24 11.1954 23.9172 10.41 23.7666 9.6498Z"
                          fill="#FFC107"
                        />
                        <path
                          d="M12.0014 23.9992C15.101 23.9992 17.9174 22.813 20.0468 20.884L16.3328 17.7412C15.0875 18.6883 13.5658 19.2005 12.0014 19.1992C8.88016 19.1992 6.22996 17.209 5.23156 14.4316L1.31836 17.4466C3.30436 21.3328 7.33756 23.9992 12.0014 23.9992Z"
                          fill="#4CAF50"
                        />
                        <path
                          d="M23.7666 9.65039H22.8V9.60059H12V14.4006H18.7818C18.3085 15.7304 17.456 16.8925 16.3296 17.7432L16.3314 17.742L20.0454 20.8848C19.7826 21.1236 24 18.0006 24 12.0006C24 11.196 23.9172 10.4106 23.7666 9.65039Z"
                          fill="#1976D2"
                        />
                        <path
                          d="M1.38281 6.4146L5.32541 9.306C6.39221 6.6648 8.97581 4.8 11.9992 4.8C13.8346 4.8 15.5044 5.4924 16.7758 6.6234L20.17 3.2292C18.0268 1.2318 15.16 0 11.9992 0C7.39001 0 3.39281 2.6022 1.38281 6.4146Z"
                          fill="#FF3D00"
                        />
                      </svg>
                    </a>
                    <a href="https://api.promusic.az/auth/facebook">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M24 12.0731C24 18.0979 19.6125 23.0945 13.875 24V15.563H16.6711L17.2031 12.0731H13.875V9.80939C13.875 8.85439 14.3391 7.92297 15.8297 7.92297H17.3438V4.95186C17.3438 4.95186 15.9703 4.71605 14.6578 4.71605C11.9156 4.71605 10.125 6.3879 10.125 9.41324V12.0731H7.07813V15.563H10.125V24C4.3875 23.0945 0 18.0979 0 12.0731C0 5.4046 5.37188 0 12 0C18.6281 0 24 5.4046 24 12.0731Z"
                          fill="white"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
                <div className="form-control loginFormControl">
                  <span className="prefix">+994</span>
                  <input
                    spellCheck="false"
                    type="number"
                    name="phone"
                    id="phone"
                    placeholder="50 000 00 00"
                    onChange={handleChange}
                  />

                  <small>
                    {/* @ts-ignore */}
                    {error?.response?.data?.errors?.phone
                      ? // @ts-ignore
                        error?.response?.data?.errors?.phone[0]
                      : "\u00A0"}
                  </small>
                </div>
                <button className="submit-btn" onClick={handleSubmit}>
                  {t("sendConfirmationCode", locale)}
                </button>

                <div className="bottom-login-class">
                  {t("haveAccount", locale)}{" "}
                  <Link href={`${localePrefix(locale)}/login`}>{t("login", locale)}</Link>
                </div>
              </form>
            ) : (
              <div className="register-body">
                <div className="register-card">
                  <div className="header">
                    <h1 className="title">{t("ConfirmYourNumber", locale)}</h1>
                    <h3 className="subtitle">{t("enterCodeLogin", locale)}</h3>
                  </div>
                  <div className="form-control">
                    <label>{t("smsCode", locale)}</label>
                    <small
                      style={{
                        marginBottom: 10,
                        display: "block",
                      }}
                    >
                      {/* @ts-ignore */}
                      {errorVerify?.response?.data?.errors
                        ? // @ts-ignore
                          errorVerify?.response?.data?.errors[0]
                        : ""}
                    </small>
                    <ReactCodeInput
                      className="code-input"
                      name="phone"
                      onChange={(e) => {
                        if (e.length === 4) setCode(e);
                      }}
                      type="number"
                      fields={4}
                      inputMode="numeric"
                    />
                  </div>
                  <button className="button submit-btn" onClick={handleVerify}>
                    {t("confirm", locale)}
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="register-body">
              <div className="register-card">
                <div className="header">
                  <h1 className="title">{t("setYourPassword", locale)}</h1>
                  <h3 className="subtitle">
                    {t("setPasswordSubtitle", locale)}
                  </h3>
                </div>
                <div className="form-control">
                  <div className="password-container">
                    <input
                      spellCheck="false"
                      type="password"
                      className="currentPassword"
                      name="currentPassword"
                      id="currentPassword"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setPassword(e.currentTarget.value)
                      }
                    />
                    <span className="eye" onClick={handleType}>
                      <img
                        src={
                          type === "password" ? EyeClosed.src : EyeClosed.src
                        }
                        alt="eye icon"
                      />
                    </span>
                  </div>
                  {/* @ts-ignore */}
                  {errorPassword?.response?.data?.errors?.password ? (
                    <small>
                      {/* @ts-ignore */}
                      {errorPassword?.response?.data?.errors?.password[0]}
                    </small>
                  ) : (
                    <></>
                  )}
                  {/* @ts-ignore */}
                  {errorPassword?.response?.data?.errors?.code ? (
                    <small>
                      {/* @ts-ignore */}
                      {errorPassword?.response?.data?.errors?.code[0]}
                    </small>
                  ) : (
                    <></>
                  )}
                  {/* @ts-ignore */}
                  {errorPassword?.response?.data?.errors?.phone ? (
                    <small>
                      {/* @ts-ignore */}
                      {errorPassword?.response?.data?.errors?.phone[0]}
                    </small>
                  ) : (
                    <></>
                  )}
                </div>
                <button
                  className="button submit-btn"
                  onClick={handleSetPassword}
                >
                  {t("startShopping", locale)}
                </button>
              </div>
            </div>
          )}
        </div>
      </Wrapper>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { locale } = ctx;
  const { auth } = nookies.get(ctx);

  try {
    if (auth) {
      await fetch(baseURL + "/profile", {
        headers: {
          "x-localization": locale ? locale : "az",
          authorization: "Bearer " + auth,
        },
      });

      return {
        redirect: {
          permanent: false,
          destination: `${localePrefix(locale)}/account/orders`,
        },
      };
    }

    return {
      props: {
        locale,
      },
    };
  } catch (error: any) {
    if (error.response.status === 401) {
      nookies.destroy(ctx, "auth", { path: "/" });
      return {
        redirect: {
          permanent: false,
          destination: `${localePrefix(locale)}/register`,
        },
      };
    }

    return {
      redirect: {
        permanent: false,
        destination: `${localePrefix(locale) || "/"}`,
      },
    };
  }
};

const Wrapper = styled.div`
  .login {
    width: 1289px;
    // margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    grid-gap: 0 2.4rem;
    margin: 4.8rem auto;
    padding-bottom: 5.6rem;

    @media screen and (max-width: 1199px) {
      width: 100%;
      padding: 0 1.6rem;
    }

    .google-facebook {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 1.2rem 0 2.4rem;

      a {
        height: 6rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        box-sizing: border-box;
        border-radius: 0.4rem;

        &:first-child {
          border: 0.1rem solid rgba(5, 6, 30, 0.07);
          margin-right: 2.4rem;
        }

        &:last-child {
          background: #1778f2;
        }
      }
    }

    &-img-container {
      grid-column: 1/4;
      width: 100%;
      height: 500px;
      background: #c4c4c4;
      border-radius: 12px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .login-form {
      grid-column: 5/8;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .header {
        padding-bottom: 2.4rem;

        .title {
          font-style: normal;
          font-weight: 500;
          font-size: 3.6rem;
          line-height: 100%;
          color: #3a4155;
          margin-bottom: 12px;
        }

        .subtitle {
          font-style: normal;
          font-weight: normal;
          font-size: 1.6rem;
          line-height: 150%;
          color: #989eaf;
          margin-bottom: 1.2rem;
        }
      }

      .form-control {
        &.loginFormControl {
          position: relative;

          #phone {
            margin-top: -20px;
          }

          .prefix {
            top: 21px;
          }
        }

        .prefix {
          width: 6rem;
          position: relative;
          margin-bottom: -3.9rem;
          padding-left: 2rem;
          font-style: normal;
          font-weight: normal;
          font-size: 2rem;
          line-height: 100%;
          color: #989eaf;
          top: 40px;

          & + input {
            padding-left: 7rem;
          }
        }

        input {
          border: 1px solid #3a4155;
          &::placeholder {
            color: #989eaf;
          }
        }

        label {
          font-style: normal;
          font-weight: normal;
          font-size: 1.6rem;
          line-height: 100%;
          color: #3a4155;

          &::placeholder {
            font-size: 1.6rem;
            color: #989eaf;
          }
        }
      }

      .submit-btn {
        height: 6rem;
        width: 100%;
        background: #444444;
        border-radius: 6px;
        font-style: normal;
        font-weight: normal;
        font-size: 1.8rem;
        line-height: 100%;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
      }
    }

    .bottom-login-class {
      margin-top: 78px;
      display: flex;
      align-items: center;
      font-style: normal;
      font-weight: normal;
      font-size: 18px;
      line-height: 100%;
      color: #1b1c1f;

      a {
        color: #ff4c4c;
        margin-left: 6px;
      }
    }

    @media screen and (max-width: 991.9px) {
      .bottom-login-class {
        margin-top: 48px;
        display: flex;
        align-items: center;
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 100%;
        color: #1b1c1f;

        a {
          color: #ff4c4c;
          margin-left: 6px;
        }
      }

      &-img-container {
        display: none;
      }

      .login-form {
        grid-column: 1/9;
      }

      .header {
        .title {
          font-size: 36px;
        }

        .subtitle {
          font-size: 16px;
        }
      }
    }

    @media screen and (max-width: 767.9px) {
      padding: 39px 16px 111px;
    }
  }

  .register .register-card .header {
    padding-bottom: 2.4rem;
  }

  .register .register-card .header .title {
    font-style: normal;
    font-weight: 500;
    font-size: 3.6rem;
    line-height: 100%;
    color: #3a4155;
    margin-bottom: 12px;
  }

  .code-input input:first-child {
    border-radius: 0;
    border-radius: 6px;
  }

  .code-input {
    width: 100%;
  }

  .register
    .register-body
    .register-card
    .form-control
    .code-input
    input:first-child {
    border-radius: 0;
    border-radius: 6px;
  }

  .code-input input {
    font-family: Nunito, sans-serif;
    border: 1px solid #d0d4de;
    border-radius: 0;
    width: 6rem;
    height: 6rem;
    font-style: normal;
    font-weight: 400;
    font-size: 1.8rem;
    line-height: 100%;
    color: #989eaf;
    border-radius: 6px;
    width: 100% !important;
    height: 6rem !important;

    &:not(:last-child) {
      margin-right: 45px;
    }
  }

  .register .register-card .header .title {
    font-style: normal;
    font-weight: 500;
    font-size: 3.6rem;
    line-height: 100%;
    color: #3a4155;
    margin-bottom: 12px;
  }

  .register .register-card .header .subtitle {
    font-style: normal;
    font-weight: 400;
    font-size: 1.6rem;
    line-height: 150%;
    color: #989eaf;
  }

  .register-card .form-control label {
    font-style: normal;
    font-weight: 400;
    font-size: 1.6rem;
    line-height: 100%;
    color: #3a4155;
  }

  .form-control small {
    font-style: normal;
    font-weight: 400;
    font-size: 1.2rem;
    line-height: 170%;
    color: #ff4c4c;
    margin-bottom: 4px;
  }

  .register .register-card .submit-btn {
    height: 6rem;
    width: 100%;
    background: #444;
    border-radius: 6px;
    font-style: normal;
    font-weight: 400;
    font-size: 1.8rem;
    line-height: 100%;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .register .register-body {
    grid-column: 5/8;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* add css styles here (optional) */

  .styles_react-code-input-container__tpiKG {
    position: relative;
  }

  .styles_react-code-input__CRulA > input {
    border: solid 1px #a8adb7;
    border-right: none;
    font-family: "Lato";
    font-size: 20px;
    color: #525461;
    text-align: center;
    box-sizing: border-box;
    border-radius: 0;
    -webkit-appearance: initial;
  }

  .styles_react-code-input__CRulA > input:last-child {
    border-right: solid 1px #a8adb7;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .styles_react-code-input__CRulA > input:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .styles_react-code-input__CRulA > input:focus {
    outline: none;
    border: 1px solid #006fff;
    caret-color: #006fff;
  }

  .styles_react-code-input__CRulA > input:focus + input {
    border-left: none;
  }

  .styles_loading__Z65VQ {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    text-align: center;
  }

  .styles_blur__19vMK {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #fff;
    opacity: 0.5;
    filter: blur(0.5px);
    transition: opacity 0.3s;
  }

  .styles_title__1cca0 {
    margin: 0;
    height: 20px;
    padding-bottom: 10px;
  }

  .styles_spin__6y_8G {
    display: inline-block;
    animation: styles_loadingCircle__293ky 1s infinite linear;
  }

  @keyframes styles_loadingCircle__293ky {
    100% {
      transform: rotate(360deg);
    }
  }

  .register-card .form-control input {
    width: calc((100% / 4) - 34px) !important;
    text-align: center;
  }

  .register-card .form-control {
    padding: 0;
  }

  #currentPassword {
    width: 100% !important;
    text-align: left !important;
  }
`;
